import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';

/**
 * CandidateEditDialogs — regroupe les 7 dialogues d'édition de la fiche candidat :
 *   summary · education · experience · skills · tools · languages · strengths
 *
 * Un seul composant, activé par la prop `editingSection` (= clé du dialog à ouvrir
 * ou null pour tout fermer). Toute la logique métier (fetch, setState, save) reste
 * dans le parent ; ce composant est strictement présentationnel.
 */
const CandidateEditDialogs = ({
  editingSection,
  setEditingSection,
  editData,
  setEditData,
  savingProfile,
  saveProfileSection,
  addListItem,
  removeListItem,
  updateListItem,
}) => {
  const closeDialog = () => setEditingSection(null);
  const saveBtn = (
    <Button
      onClick={saveProfileSection}
      disabled={savingProfile}
      className="bg-tp-primary hover:bg-tp-primary-light"
    >
      {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
      Enregistrer
    </Button>
  );

  return (
    <>
      {/* Edit Summary Dialog */}
      <Dialog open={editingSection === 'summary'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le résumé</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Résumé du profil</Label>
            <Textarea
              value={editData.summary || ''}
              onChange={(e) => setEditData({ ...editData, summary: e.target.value })}
              rows={6}
              placeholder="Décrivez le profil du candidat..."
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Education Dialog */}
      <Dialog open={editingSection === 'education'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier la formation</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-96 overflow-y-auto">
            {(editData.education || []).map((edu, index) => (
              <div key={`edit-edu-${index}`} className="p-4 border border-tp-border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-tp-text">Formation {index + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeListItem('education', index)} className="text-tp-error h-6 w-6 p-0">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <Input
                  placeholder="Diplôme (ex: Master en Marketing)"
                  value={edu.degree}
                  onChange={(e) => updateListItem('education', index, 'degree', e.target.value)}
                />
                <Input
                  placeholder="Établissement (ex: Université d'Antananarivo)"
                  value={edu.institution}
                  onChange={(e) => updateListItem('education', index, 'institution', e.target.value)}
                />
                <Input
                  placeholder="Année (ex: 2020)"
                  value={edu.year}
                  onChange={(e) => updateListItem('education', index, 'year', e.target.value)}
                />
              </div>
            ))}
            <Button variant="outline" onClick={() => addListItem('education')} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une formation
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Experience Dialog */}
      <Dialog open={editingSection === 'experience'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier l&apos;expérience professionnelle</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-96 overflow-y-auto">
            {(editData.work_experience || []).map((exp, index) => (
              <div key={`edit-exp-${index}`} className="p-4 border border-tp-border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-tp-text">Expérience {index + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeListItem('work_experience', index)} className="text-tp-error h-6 w-6 p-0">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <Input
                  placeholder="Poste (ex: Community Manager)"
                  value={exp.title}
                  onChange={(e) => updateListItem('work_experience', index, 'title', e.target.value)}
                />
                <Input
                  placeholder="Entreprise"
                  value={exp.company}
                  onChange={(e) => updateListItem('work_experience', index, 'company', e.target.value)}
                />
                <Input
                  placeholder="Durée (ex: Janvier 2020 - Présent)"
                  value={exp.duration}
                  onChange={(e) => updateListItem('work_experience', index, 'duration', e.target.value)}
                />
                <Textarea
                  placeholder="Description des missions..."
                  value={exp.description}
                  onChange={(e) => updateListItem('work_experience', index, 'description', e.target.value)}
                  rows={2}
                />
              </div>
            ))}
            <Button variant="outline" onClick={() => addListItem('work_experience')} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une expérience
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Skills Dialog */}
      <Dialog open={editingSection === 'skills'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier les compétences</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Compétences (séparées par des virgules)</Label>
            <Textarea
              value={editData.skills || ''}
              onChange={(e) => setEditData({ ...editData, skills: e.target.value })}
              rows={4}
              placeholder="Ex: Meta Ads, Google Ads, Community Management, Excel..."
            />
            <p className="text-xs text-tp-text-light mt-2">Séparez chaque compétence par une virgule</p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tools Dialog */}
      <Dialog open={editingSection === 'tools'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier les outils maîtrisés</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Outils (séparés par des virgules)</Label>
            <Textarea
              value={editData.tools || ''}
              onChange={(e) => setEditData({ ...editData, tools: e.target.value })}
              rows={4}
              placeholder="Ex: Canva, Photoshop, Excel, Notion, Slack, Trello..."
            />
            <p className="text-xs text-tp-text-light mt-2">Séparez chaque outil par une virgule</p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Languages Dialog */}
      <Dialog open={editingSection === 'languages'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier les langues</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-96 overflow-y-auto">
            {(editData.languages || []).map((lang, index) => (
              <div key={`lang-${lang.language}-${index}`} className="flex gap-3 items-center">
                <Input
                  placeholder="Langue (ex: Anglais)"
                  value={lang.language}
                  onChange={(e) => updateListItem('languages', index, 'language', e.target.value)}
                  className="flex-1"
                />
                <Select value={lang.level} onValueChange={(v) => updateListItem('languages', index, 'level', v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Natif">Natif</SelectItem>
                    <SelectItem value="Courant">Courant</SelectItem>
                    <SelectItem value="Intermédiaire">Intermédiaire</SelectItem>
                    <SelectItem value="Basique">Basique</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => removeListItem('languages', index)} className="text-tp-error h-8 w-8 p-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => addListItem('languages')} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une langue
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Strengths Dialog */}
      <Dialog open={editingSection === 'strengths'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier les points forts</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Points forts / Qualités (un par ligne)</Label>
            <Textarea
              value={editData.strengths || ''}
              onChange={(e) => setEditData({ ...editData, strengths: e.target.value })}
              rows={6}
              placeholder="Autonome&#10;Rigoureux&#10;Bonne communication&#10;..."
            />
            <p className="text-xs text-tp-text-light mt-2">Un point fort par ligne</p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            {saveBtn}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CandidateEditDialogs;
